import { deserializeActionData, type SerializedActionPayload } from '@/lib/actions/serialization';
import { isRecord } from '@/lib/utils/type-guards';

type SafeActionResultLike<T> = {
  data?: T | SerializedActionPayload;
  serverError?: unknown;
  validationErrors?: unknown;
};

export function unwrapSafeActionResult<T>(result: unknown): T {
  if (!isResultEnvelope(result)) {
    throw new Error('Safe action response was malformed.');
  }

  const envelope: SafeActionResultLike<T> = result;

  if (envelope.serverError) {
    throw typeof envelope.serverError === 'string'
      ? new Error(envelope.serverError)
      : new Error('Safe action failed with an unknown server error.');
  }

  if (envelope.validationErrors) {
    throw new Error('Safe action validation failed.');
  }

  if ('data' in envelope) {
    if (envelope.data === undefined) {
      throw new Error('Safe action response was missing data.');
    }
    return deserializeActionData<T>(envelope.data);
  }

  throw new Error('Safe action response was missing data.');
}

const isResultEnvelope = (value: unknown): value is Record<string, unknown> => isRecord(value);
